import { useState, type FC } from 'react'
import { Grid, TextField } from '@mui/material'
import Autocomplete from '@mui/material/Autocomplete'

import { factsGenderIdentities, factsPronouns, insuranceEmployer } from '@fireflyhealth/api'
import { useQuery } from '~/shared/hooks/query'
import { EditField } from '~/shared/ui'
import { useFeatureAccess } from '~/shared/hooks/useFeatureAccess'
import { ValueSetter } from '~/shared/types'
import { verifyMember } from './utils'
import { useDispatch } from 'react-redux'
import { queueNotification } from '~/shared/notifications/actions'
import { logger } from '~/shared/lib/logger'

interface EditPersonFieldsProps {
  first_name: string
  last_name: string
  dob: string
  preferred_name: string
  employer: number | null
  gender: string[] | null
  sex: string
  pronouns: string | null
  loading: boolean
  isUser: boolean
  isNewMember?: boolean
  patientId?: number | null
  memberIdentifierType?: string | null
  setValues: ValueSetter
  setFieldValue: (name: string, value: string | string[] | null) => Promise<unknown>
  setFieldTouched: (name: string) => void
}

const GENDERS = ['Unknown', 'Female', 'Male']

const EditPersonFields: FC<EditPersonFieldsProps> = props => {
  const [verificationCode, setVerificationCode] = useState('')
  const dispatch = useDispatch()
  // Reference data is effectively static for the session; keep cache warm to
  // avoid a refetch each time the user re-mounts the Profile tab.
  const employersQuery = useQuery(
    insuranceEmployer.listQueryKey(),
    async () => (await insuranceEmployer.list()).data,
    { staleTime: Infinity },
    { error: 'Failed to fetch employers' },
  )
  const pronounsQuery = useQuery(
    factsPronouns.listQueryKey(),
    async () => (await factsPronouns.list()).data,
    { staleTime: Infinity },
    { error: 'Failed to fetch pronouns' },
  )
  const genderIdentitiesQuery = useQuery(
    factsGenderIdentities.listQueryKey(),
    async () => (await factsGenderIdentities.list()).data,
    { staleTime: Infinity },
    { error: 'Failed to fetch gender identities' },
  )
  const featureAccess = useFeatureAccess()?.tenant
  const {
    first_name: firstName,
    last_name: lastName,
    dob,
    preferred_name: preferredName,
    employer,
    sex,
    gender,
    pronouns,
    isUser,
    isNewMember,
    setValues,
    setFieldValue,
    setFieldTouched,
  } = props

  function getFieldName(name: string) {
    if (isUser == true) name = 'person.' + name
    return name
  }

  const handleMemberVerification = async () => {
    const payload = {
      person_id: String(props.patientId),
      member_identification_number: verificationCode,
    }
    // This runs from a fire-and-forget button onClick, so a rejected request
    // (network / 4xx / 5xx) would otherwise surface as an unhandled rejection
    // with no user feedback. Catch it and show a distinct error toast, keeping
    // the legitimate "not verified" branch (a 200 with is_verified: false)
    // separate from a failed request.
    try {
      const response = await verifyMember(payload)
      if (response.is_verified) {
        dispatch(
          queueNotification({ variant: 'success', message: 'SSN or Employee ID is verified' }),
        )
        logger.info(
          'SSN or Employee ID is verified for person',
          props.patientId,
          'as',
          verificationCode,
        )
      } else {
        dispatch(
          queueNotification({ variant: 'error', message: 'SSN or Employee ID is not verified' }),
        )
        logger.info(
          'SSN or Employee ID verification failed for person',
          props.patientId,
          'with value',
          verificationCode,
        )
      }
    } catch (error) {
      dispatch(
        queueNotification({ variant: 'error', message: 'Problem verifying SSN or Employee ID' }),
      )
      logger.error(
        'SSN or Employee ID verification request failed for person',
        props.patientId,
        error,
      )
    }
  }

  return (
    <>
      <Grid container spacing={2}>
        <EditField
          {...setValues('first_name', firstName)}
          label="First Name"
          required={featureAccess?.person_matching_during_signup}
        />
        <EditField
          {...setValues('last_name', lastName)}
          label="Last Name"
          required={featureAccess?.person_matching_during_signup}
        />
        <EditField
          {...setValues(getFieldName('preferred_name'), preferredName)}
          label="Preferred Name"
        />
        <EditField
          {...setValues(getFieldName('dob'), dob)}
          label="Date of Birth"
          type="date"
          required={featureAccess?.person_matching_during_signup}
          slotProps={{ inputLabel: { shrink: true } }}
        />
        <EditField
          {...setValues(getFieldName('sex'), sex)}
          label="Sex Assigned at Birth"
          select
          required={featureAccess?.person_matching_during_signup}
          slotProps={{ select: { native: true } }}
        >
          {GENDERS.map(v => (
            <option key={v} value={v}>
              {v}
            </option>
          ))}
        </EditField>
        <Grid size={12}>
          <Grid container spacing={2}>
            <EditField
              {...setValues(getFieldName('employer'), employer)}
              label="Employer"
              select
              disabled={isNewMember}
              slotProps={{ inputLabel: { shrink: true }, select: { native: true } }}
            >
              <option value="">(none)</option>
              {employersQuery.data?.map(employer => (
                <option key={employer.id} value={employer.id}>
                  {employer.name}
                </option>
              ))}
            </EditField>

            <EditField
              onChange={e => {
                setVerificationCode(e.target.value)
              }}
              label={
                props.memberIdentifierType
                  ? props.memberIdentifierType == 'SSN'
                    ? 'Last 4 of SSN'
                    : 'Last 4 of Employee ID'
                  : ''
              }
              onChangeButtonClick={() => void handleMemberVerification()}
              variant="standard"
              showChangeButton={true}
              changeButtonLabel={'Verify'}
              value={verificationCode}
              changeButtonSize={'small'}
              changeButtonDisabled={props.memberIdentifierType ? false : true}
              slotProps={{
                input: props.memberIdentifierType ? { readOnly: false } : { readOnly: true },
              }}
            ></EditField>
          </Grid>
        </Grid>
        <Grid size={6}>
          <Autocomplete
            // Only one value will be returned from the backend for the gender field.
            // Right now in DB the gender field is a array of varchar[],
            // once it is migrated to varchar the same will be updated here.
            value={gender && gender.length > 0 ? gender[0] : null}
            onChange={(_, val) => {
              // Clearing the field clears the stored gender; `[null]` would save an array holding one null.
              void setFieldValue(getFieldName('gender'), val ? [val] : null)
              setFieldTouched(getFieldName('gender'))
            }}
            options={genderIdentitiesQuery.data?.map(g => g.label) ?? []}
            disabled={props.loading}
            renderInput={params => <TextField {...params} label="Gender Identity" />}
          />
        </Grid>
        <Grid size={6}>
          <Autocomplete
            onInputChange={(_, val) => {
              void setFieldValue(getFieldName('pronouns'), val)
              setFieldTouched(getFieldName('pronouns'))
            }}
            value={pronouns ?? ''}
            disabled={props.loading}
            options={pronounsQuery.data?.map(p => p.label) ?? []}
            freeSolo
            renderInput={params => <TextField {...params} label="Preferred Pronouns" />}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default EditPersonFields
